import { useEffect, useState } from 'react';
import { ExternalLinkIcon, PackageIcon, PackageOpenIcon, StethoscopeIcon } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Dialog,
    DialogBody,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { InvoicesList } from '@/components/InvoicesList';
import { useAuth } from '@/context/AuthContext';
import { useFetch } from '@/hooks/useFecth';
import server from '@/services/server';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';
import { getSubscriptionStatusInfo } from '../utils/subscriptionStatus';
import { AsaasMigrationTab, SubscriptionTab } from './SubscriptionManagementTabs';

function formatDate(dateString) {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

function formatCurrency(value) {
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const paymentMethods = {
    CREDIT_CARD: 'Cartão de Crédito',
    PIX: 'PIX',
    BOLETO: 'Boleto',
    MONEY: 'Dinheiro',
    DEBIT_CARD: 'Cartão de Débito',
};

function DetailField({ label, children, className }) {
    return (
        <div className={cn('flex flex-col gap-1', className)}>
            <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{label}</span>
            <div className="text-sm font-medium">{children}</div>
        </div>
    );
}

function SubscriptionPaymentsTab({ subscriber }) {
    const { data: invoices, isLoading } = useFetch(
        subscriber ? `club/subscribers/${subscriber.id}/invoices/asaas` : null,
    );

    return <InvoicesList club={subscriber?.club} data={invoices} isLoading={isLoading} checkbox={false} />;
}

function SubscriptionDependentsTab({ subscriber }) {
    const personId = subscriber?.Person?.id || subscriber?.personId || subscriber?.id;
    const { data, isLoading } = useFetch(personId ? `club/subscribers?dependentBy=${personId}` : null);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center gap-2 py-10">
                <Spinner className="size-5" />
                <span className="text-sm text-muted-foreground">Carregando dependentes...</span>
            </div>
        );
    }

    const dependents = data?.rows || [];

    if (!dependents.length) {
        return (
            <p className="py-10 text-center text-sm text-muted-foreground">Nenhum dependente cadastrado.</p>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            <p className="font-semibold">Dependentes ({dependents.length})</p>
            <div className="divide-y rounded-xl border">
                {dependents.map(dependent => (
                    <div key={dependent.id} className="flex items-start gap-3 p-4">
                        <Avatar className="size-10">
                            <AvatarFallback
                                className={cn(
                                    dependent?.status
                                        ? 'bg-primary/10 text-primary'
                                        : 'bg-muted text-muted-foreground',
                                )}
                            >
                                {dependent?.name?.charAt(0)?.toUpperCase() || 'D'}
                            </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1 space-y-1">
                            <p className="font-medium">{dependent.name || 'N/A'}</p>
                            {dependent.document ? (
                                <p className="text-sm text-muted-foreground">CPF: {dependent.document}</p>
                            ) : null}
                            {dependent.email ? (
                                <p className="text-sm text-muted-foreground">Email: {dependent.email}</p>
                            ) : null}
                            {dependent.phone ? (
                                <p className="text-sm text-muted-foreground">Telefone: {dependent.phone}</p>
                            ) : null}
                            {dependent.birthday ? (
                                <p className="text-sm text-muted-foreground">
                                    Aniversário: {formatDate(dependent.birthday)}
                                </p>
                            ) : null}
                        </div>
                        <Badge variant={dependent?.status ? 'default' : 'secondary'}>
                            {dependent?.status ? 'Ativo' : 'Inativo'}
                        </Badge>
                    </div>
                ))}
            </div>
        </div>
    );
}

const CYCLE_LABELS = {
    MONTHLY: 'Mensal',
    YEARLY: 'Anual',
};

const REFUND_STATUS_LABELS = {
    REQUESTED: 'Solicitado',
    IN_PROGRESS: 'Em andamento',
    DONE: 'Concluído',
    PARTIAL: 'Parcial concluído',
    FAILED: 'Falhou',
};

const CANCEL_REASON_LABELS = {
    WITHDRAWAL: 'Arrependimento (7 dias)',
    CUSTOMER: 'Pedido do assinante',
    CLUB: 'Decisão do clube',
    CHARGEBACK: 'Contestação no cartão',
};

const RECEIVED_STATUSES = ['RECEIVED', 'RECEIVED_IN_CASH'];

function resolveInstallmentInfo(subscription) {
    const isInstallment =
        String(subscription?.billingModel || '').toUpperCase() === 'INSTALLMENT' ||
        Boolean(subscription?.installmentExternalId);
    if (!isInstallment) return null;
    const rows = Array.isArray(subscription?.installments) ? subscription.installments : [];
    const current = rows.find(row => row.externalId === subscription?.installmentExternalId) || rows[0] || null;
    const invoices = current?.invoices || [];
    return {
        count: current?.installmentCount || invoices.length || null,
        value: current?.installmentValue ?? null,
        total: current?.totalValue ?? subscription?.value ?? null,
        received: invoices.filter(inv => RECEIVED_STATUSES.includes(String(inv?.status || '').toUpperCase())).length,
        refundedValue: Number(current?.refundedValue || 0),
        cycleEndAt: current?.cycleEndAt || subscription?.dueDateCycle || null,
    };
}

function DependentGeneralTab({ subscriber }) {
    const holder = subscriber?.DependentBy;
    return (
        <div className="flex flex-col gap-4">
            <div className="rounded-lg border px-4 py-3 text-sm text-muted-foreground">
                Dependente{holder?.name ? ` de ${holder.name}` : ''}
                {holder?.document ? ` (${holder.document})` : ''}. Plano, status e benefícios acompanham a assinatura
                do titular.
            </div>
            <Card>
                <CardContent className="grid min-w-0 gap-4 p-4 sm:grid-cols-2">
                    <DetailField label="Titular">{holder?.name || '—'}</DetailField>
                    <DetailField label="Vínculo desde">{formatDate(subscriber?.subscription?.createdAt)}</DetailField>
                </CardContent>
            </Card>
        </div>
    );
}

function hasAssignedPlan(subscription) {
    return Boolean(Number(subscription?.planId) || subscription?.planData);
}

function NoPlanGeneralTab({ hasReseller, clubHasAsaas, onAssignPlan }) {
    const ways = hasReseller
        ? [
              clubHasAsaas
                  ? 'Ele pode aderir a um plano pelo site ou app do clube (pagamento online).'
                  : null,
              'Você pode atribuir um plano na aba Assinatura. O plano entra como pacote em dinheiro: nenhuma cobrança é gerada pela plataforma e o recebimento fica por conta do clube.',
              'Ou pela importação por planilha / API informando o SKU do plano.',
          ]
        : [
              'Atribua um plano na aba Assinatura.',
              'Ou pela importação por planilha / API informando o SKU do plano.',
          ];

    return (
        <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed px-6 py-10 text-center">
            <PackageOpenIcon className="size-10 text-muted-foreground" />
            <div className="space-y-1">
                <p className="font-semibold">Este assinante ainda não tem um plano</p>
                <p className="text-sm text-muted-foreground">
                    {hasReseller
                        ? 'Sem plano, o acesso ao clube fica bloqueado até ele ganhar um plano.'
                        : 'Sem plano, ele não recebe os benefícios do clube até ganhar um plano.'}
                </p>
            </div>
            <ul className="list-disc space-y-1 pl-5 text-left text-sm text-muted-foreground">
                {ways.filter(Boolean).map(way => (
                    <li key={way}>{way}</li>
                ))}
            </ul>
            <Button type="button" onClick={onAssignPlan}>
                Atribuir plano
            </Button>
        </div>
    );
}

/** Super: mesmo "Iniciar atendimento" do site do clube (auth link da Click Life), para testar o fluxo. */
function TelemedicineAuthLinkCard({ personId }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const [opening, setOpening] = useState(false);

    const handleOpen = async () => {
        const popup = window.open('', '_blank');
        setOpening(true);
        try {
            const { data: response } = await server.post(`club/subscribers/${personId}/telemedicina/auth-link`);
            const link = response?.link;
            if (!link) throw { message: 'A telemedicina não devolveu o link de acesso.' };
            if (popup) {
                popup.opener = null;
                popup.location.href = link;
            } else {
                window.open(link, '_blank', 'noopener,noreferrer');
            }
            enqueueSnackbar(response?.mode === 'url' ? 'Abrindo a URL própria do clube.' : 'Atendimento aberto.');
        } catch (error) {
            popup?.close();
            setErrors(error);
        } finally {
            setOpening(false);
        }
    };

    return (
        <Card className="border-dashed">
            <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                    <StethoscopeIcon className="mt-0.5 size-5 text-primary" />
                    <div>
                        <p className="font-semibold">Telemedicina</p>
                        <p className="text-sm text-muted-foreground">
                            Abre o atendimento como o assinante, igual ao botão do site do clube. Visível só para
                            super.
                        </p>
                    </div>
                </div>
                <Button type="button" onClick={handleOpen} disabled={opening} className="shrink-0">
                    {opening ? <Spinner className="size-4" /> : <ExternalLinkIcon className="size-4" />}
                    {opening ? 'Abrindo atendimento...' : 'Iniciar atendimento'}
                </Button>
            </CardContent>
        </Card>
    );
}

function SubscriptionGeneralTab({
    subscriber,
    club,
    hasReseller,
    showPlanUi,
    clubHasAsaas,
    onAssignPlan,
    telemedicinePersonId,
}) {
    const benefits = subscriber?.subscription?.benefits || [];
    const statusInfo = getSubscriptionStatusInfo(subscriber?.subscription);
    const cycle =
        String(subscriber?.subscription?.cycle || '').toUpperCase() === 'YEARLY' ? 'YEARLY' : 'MONTHLY';
    const installment = resolveInstallmentInfo(subscriber?.subscription);

    if (subscriber?.dependentBy) return <DependentGeneralTab subscriber={subscriber} />;

    const subscriptionStatus = String(subscriber?.subscription?.status || '').toUpperCase();
    if (showPlanUi && subscriptionStatus !== 'CANCELLED' && !hasAssignedPlan(subscriber?.subscription)) {
        return (
            <NoPlanGeneralTab hasReseller={hasReseller} clubHasAsaas={clubHasAsaas} onAssignPlan={onAssignPlan} />
        );
    }

    return (
        <div className="flex flex-col gap-6">
            {statusInfo.description ? (
                <div
                    className={cn(
                        'rounded-lg border px-4 py-3 text-sm',
                        statusInfo.tone === 'danger'
                            ? 'border-destructive/40 bg-destructive/5 text-destructive'
                            : 'border-amber-400/40 bg-amber-50 text-amber-700',
                    )}
                >
                    {statusInfo.description}
                </div>
            ) : null}

            <Card>
                <CardContent className="grid min-w-0 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
                    <DetailField label="ID da Assinatura">{subscriber?.subscription?.id ?? '—'}</DetailField>
                    <DetailField label="Adesão">{formatDate(subscriber?.subscription?.createdAt)}</DetailField>
                    <DetailField label={hasReseller ? 'Cancelamento' : 'Inativação'}>
                        {formatDate(subscriber?.subscription?.canceledAt)}
                    </DetailField>
                    <DetailField label="Valor Total">
                        <span className={hasReseller ? 'text-primary' : undefined}>
                            {hasReseller ? formatCurrency(subscriber?.subscription?.value || 0) : '—'}
                        </span>
                        {hasReseller ? (
                            <span className="text-xs text-muted-foreground">
                                {cycle === 'YEARLY' ? ' /ano' : ' /mês'}
                            </span>
                        ) : null}
                    </DetailField>

                    {hasReseller ? (
                        <DetailField label="Ciclo">
                            <Badge variant={cycle === 'YEARLY' ? 'default' : 'secondary'}>
                                {CYCLE_LABELS[cycle]}
                            </Badge>
                        </DetailField>
                    ) : null}

                    {hasReseller ? (
                        <DetailField label={installment ? 'Acesso até' : 'Vencimento do ciclo'}>
                            {formatDate(installment?.cycleEndAt || subscriber?.subscription?.dueDateCycle)}
                        </DetailField>
                    ) : null}

                    {hasReseller ? (
                        <DetailField label="Forma de pagamento">
                            {paymentMethods[String(subscriber?.subscription?.billingType || '').toUpperCase()] ||
                                'Não informado'}
                        </DetailField>
                    ) : null}

                    {hasReseller ? (
                        <DetailField label="Modelo de cobrança">
                            {installment ? (
                                <>
                                    <Badge variant="outline">
                                        Anual parcelado {installment.count ? `${installment.count}x` : ''}
                                    </Badge>
                                    {installment.value ? (
                                        <span className="block text-xs text-muted-foreground">
                                            {installment.count}x de {formatCurrency(installment.value)} · sem renovação
                                            automática
                                        </span>
                                    ) : null}
                                </>
                            ) : String(subscriber?.subscription?.billingType || '').toUpperCase() === 'MONEY' ? (
                                <>
                                    <Badge variant="secondary">Pacote em dinheiro</Badge>
                                    <span className="block text-xs text-muted-foreground">
                                        Sem cobrança pela plataforma
                                    </span>
                                </>
                            ) : (
                                <>
                                    <Badge variant="secondary">Recorrente</Badge>
                                    <span className="block text-xs text-muted-foreground">
                                        Renova automaticamente a cada {cycle === 'YEARLY' ? 'ano' : 'mês'}
                                    </span>
                                </>
                            )}
                        </DetailField>
                    ) : null}

                    {installment ? (
                        <>
                            <DetailField label="Parcelas recebidas">
                                {installment.received} de {installment.count || '—'}
                                <span className="block text-xs text-muted-foreground">
                                    Todas autorizadas no cartão; o repasse é mensal.
                                </span>
                            </DetailField>
                        </>
                    ) : null}

                    {subscriber?.subscription?.refundStatus ? (
                        <DetailField label="Estorno">
                            <Badge variant={subscriber.subscription.refundStatus === 'FAILED' ? 'destructive' : 'secondary'}>
                                {REFUND_STATUS_LABELS[subscriber.subscription.refundStatus] ||
                                    subscriber.subscription.refundStatus}
                            </Badge>
                            {installment?.refundedValue ? (
                                <span className="block text-xs text-muted-foreground">
                                    {formatCurrency(installment.refundedValue)} estornado
                                </span>
                            ) : null}
                        </DetailField>
                    ) : null}

                    {subscriber?.subscription?.cancelReason ? (
                        <DetailField label="Motivo do cancelamento">
                            {CANCEL_REASON_LABELS[subscriber.subscription.cancelReason] ||
                                subscriber.subscription.cancelReason}
                            {subscriber.subscription.cancelNote ? (
                                <span className="block text-xs font-normal text-muted-foreground">
                                    {subscriber.subscription.cancelNote}
                                </span>
                            ) : null}
                        </DetailField>
                    ) : null}

                    {subscriber?.subscription?.scheduledPlanId ? (
                        <DetailField label="Troca agendada">
                            Plano #{subscriber.subscription.scheduledPlanId}
                            {subscriber.subscription.scheduledBillingModel === 'INSTALLMENT' ? ' · parcelado' : ''}
                            <span className="block text-xs text-muted-foreground">
                                Início em {formatDate(subscriber.subscription.scheduledStartAt)}
                            </span>
                        </DetailField>
                    ) : null}

                    {showPlanUi ? (
                    <DetailField label="Plano" className="min-w-0 sm:col-span-2 lg:col-span-1">
                        <div className="flex min-w-0 items-center gap-2">
                            <Avatar className="size-7 shrink-0">
                                <AvatarImage
                                    src={subscriber?.subscription?.Plan?.image}
                                    alt={subscriber?.subscription?.Plan?.name}
                                />
                                <AvatarFallback>P</AvatarFallback>
                            </Avatar>
                            <span className="truncate">
                                {subscriber?.subscription?.planData?.name || 'N/A'}
                            </span>
                        </div>
                    </DetailField>
                    ) : null}

                    <DetailField label="Status da Assinatura">
                        <Badge variant={statusInfo.badgeVariant}>{statusInfo.label}</Badge>
                    </DetailField>
                </CardContent>
            </Card>

            {telemedicinePersonId ? <TelemedicineAuthLinkCard personId={telemedicinePersonId} /> : null}

            {benefits.length > 0 ? (
                <div className="flex flex-col gap-3">
                    <p className="font-semibold">Produtos Adquiridos</p>
                    <Separator />
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {benefits.map(benefit => (
                            <Card key={benefit.id} className="bg-muted/30">
                                <CardContent className="flex flex-col gap-2 p-4">
                                    <div className="flex items-center gap-2">
                                        <PackageIcon className="size-5 text-primary" />
                                        <p className="font-semibold">{benefit.aditionalName || 'Produto'}</p>
                                    </div>
                                    {benefit?.resaleProduct?.price != null ? (
                                        <p className="text-sm text-muted-foreground">
                                            Valor unitário:{' '}
                                            <span className="font-semibold text-primary">
                                                {formatCurrency(benefit.resaleProduct.price || 0)}
                                            </span>
                                        </p>
                                    ) : null}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            ) : null}

        </div>
    );
}

function mergeSubscriberDetail(subscriber, person) {
    if (!person) return subscriber;
    return {
        ...subscriber,
        ...person,
        subscription: person.subscription
            ? { ...subscriber?.subscription, ...person.subscription }
            : subscriber?.subscription,
    };
}

export default function SubscriptionDetailsModal({ club, open, onClose, subscriber, onChanged }) {
    const [tab, setTab] = useState('general');
    const [assignOpen, setAssignOpen] = useState(false);
    const { user } = useAuth();
    const { club: currentClub } = useClub();
    const functions = club?.functions || subscriber?.Club?.functions || currentClub?.functions || [];
    const hasReseller = Array.isArray(functions) && functions.includes('reseller');
    const hasBenefits = Array.isArray(functions) && functions.includes('benefit');
    const showPlanUi = hasReseller || hasBenefits;
    const showAsaasTab = Boolean(user?.super) && Boolean(currentClub?.hasAsaasKey);

    const personId = subscriber?.Person?.id || subscriber?.personId || subscriber?.id;
    const { data: person, mutate: mutatePerson } = useFetch(open && personId ? `club/subscribers/${personId}` : null);
    const detail = mergeSubscriberDetail(subscriber, person);
    const isDependent = Boolean(detail?.dependentBy);
    const showPayments = hasReseller && !isDependent;

    useEffect(() => {
        if (!open) return;
        setTab('general');
        setAssignOpen(false);
    }, [open, subscriber?.id]);

    const refreshAll = async () => {
        await mutatePerson?.();
        await onChanged?.();
    };

    if (!open) return null;

    return (
        <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
            <ScrollDialogContent className="w-[min(100vw-2rem,56rem)] max-w-none sm:max-w-4xl">
                <ScrollDialogHeader>
                    <DialogTitle>Detalhes da Assinatura</DialogTitle>
                </ScrollDialogHeader>

                <DialogBody className="min-w-0 overflow-x-hidden">
                    <Tabs value={tab} onValueChange={setTab} className="min-w-0">
                        <TabsList className="mb-4 flex h-auto w-full flex-wrap gap-1">
                            <TabsTrigger value="general" className="flex-1">
                                Informações Gerais
                            </TabsTrigger>
                            <TabsTrigger value="subscription" className="flex-1">
                                Assinatura
                            </TabsTrigger>
                            {showPayments ? (
                                <TabsTrigger value="payments" className="flex-1">
                                    Pagamentos
                                </TabsTrigger>
                            ) : null}
                            {!isDependent ? (
                                <TabsTrigger value="dependents" className="flex-1">
                                    Dependentes
                                </TabsTrigger>
                            ) : null}
                            {showAsaasTab ? (
                                <TabsTrigger value="asaas" className="flex-1">
                                    Asaas
                                </TabsTrigger>
                            ) : null}
                        </TabsList>

                        <TabsContent value="general" className="min-w-0">
                            <SubscriptionGeneralTab
                                subscriber={detail}
                                club={club}
                                hasReseller={hasReseller}
                                showPlanUi={showPlanUi}
                                clubHasAsaas={Boolean(currentClub?.hasAsaasKey)}
                                telemedicinePersonId={user?.super ? personId : null}
                                onAssignPlan={() => {
                                    setTab('subscription');
                                    setAssignOpen(true);
                                }}
                            />
                        </TabsContent>

                        <TabsContent value="subscription" className="min-w-0">
                            {person ? (
                                <SubscriptionTab
                                    mutate={refreshAll}
                                    initialData={person}
                                    onUnlinkDependent={refreshAll}
                                    assignOpen={assignOpen}
                                    onAssignOpenChange={setAssignOpen}
                                />
                            ) : (
                                <div className="flex items-center justify-center gap-2 py-10">
                                    <Spinner className="size-5" />
                                    <span className="text-sm text-muted-foreground">Carregando assinatura...</span>
                                </div>
                            )}
                        </TabsContent>

                        {showPayments ? (
                            <TabsContent value="payments" className="min-w-0 overflow-x-auto">
                                <SubscriptionPaymentsTab subscriber={detail} />
                            </TabsContent>
                        ) : null}

                        {!isDependent ? (
                            <TabsContent value="dependents" className="min-w-0">
                                <SubscriptionDependentsTab subscriber={detail} />
                            </TabsContent>
                        ) : null}

                        {showAsaasTab ? (
                            <TabsContent value="asaas" className="min-w-0">
                                {person ? <AsaasMigrationTab initialData={person} mutatePerson={refreshAll} /> : null}
                            </TabsContent>
                        ) : null}
                    </Tabs>
                </DialogBody>

                <ScrollDialogFooter>
                    <Button type="button" variant="outline" onClick={onClose}>
                        Fechar
                    </Button>
                </ScrollDialogFooter>
            </ScrollDialogContent>
        </Dialog>
    );
}
